import { Button } from '@mantine/core'
import { IconCheck, IconX } from '@tabler/icons-react'
import { initFirebaseApp } from '@legacy/common/Tools/Firebase'
import axios from 'axios'
import { notifications } from 'context'
import firebase from 'firebase/app'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FirebaseTokenManager } from '../../TeacherSignup/service/types'
import { connectGoogle } from '../service/profileService'

const NOTIFICATION_ICON_SIZE = 18

interface GoogleConnectButtonProps {
  userEmail: string
  onSuccess: () => void
}

/**
 * Button that opens a Firebase Google popup and links the account to the user's profile.
 * Follows Google branding guidelines: "Continue with Google" text + standard color "G" icon.
 */
export function GoogleConnectButton({ userEmail, onSuccess }: GoogleConnectButtonProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    initFirebaseApp()
  }, [])

  async function handleConnect() {
    setLoading(true)

    try {
      const provider = new firebase.auth.GoogleAuthProvider()
      const result = await firebase.auth().signInWithPopup(provider)

      if (!result.user) {
        throw new Error(t('my_profile.connect_failed'))
      }

      const { user } = result

      // Try internal stsTokenManager first (same pattern as GoogleSignupButton),
      // fall back to official Firebase API methods if unavailable
      const internalToken = (user as any).stsTokenManager as FirebaseTokenManager | undefined
      const tokenManager: FirebaseTokenManager = internalToken?.expirationTime
        ? internalToken
        : {
          accessToken: await user.getIdToken(),
          refreshToken: user.refreshToken || '',
          expirationTime: Date.now() + 3600000,
        }

      await connectGoogle(
        {
          email: user.email || '',
          uid: user.uid,
          displayName: user.displayName || undefined,
          photoURL: user.photoURL || undefined,
        },
        tokenManager,
      )

      notifications.show({
        message: t('my_profile.connect_success'),
        color: 'green',
        icon: <IconCheck size={NOTIFICATION_ICON_SIZE} />,
      })

      onSuccess()
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return
      }

      const message = getErrorMessage(error, t, userEmail)
      notifications.show({
        message,
        color: 'red',
        icon: <IconX size={NOTIFICATION_ICON_SIZE} />,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      variant='outline'
      color='dark'
      size='sm'
      compact
      loading={loading}
      onClick={handleConnect}
    >
      {t('my_profile.connect_google')}
    </Button>
  )
}

function getErrorMessage(error: any, t: (key: string, opts?: any) => string, userEmail: string): string {
  if (error.code === 'auth/popup-blocked') return t('my_profile.popup_blocked')
  if (error.code === 'auth/network-request-failed') return t('my_profile.network_error')

  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const data = error.response?.data as Record<string, string[]> | undefined

    if (status === 401) return t('my_profile.already_connected')

    if (status === 422 && data) {
      if (data.email) return t('my_profile.email_mismatch', { email: userEmail })
      if (data.uid) return t('my_profile.uid_already_used')
      if (data.token) return t('my_profile.token_expired')
    }
  }

  return t('my_profile.connect_failed')
}
